import React, { useEffect, useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { navigate } from "vike/client/router"
import PubSub from "pubsub-js"
import Navbar from "@components/Navbar"
import { listOrgs } from "aldoo-ra/Organizations/api"
import { listProjects, listComponents } from "../api"
import DataTable from "aldoo-ra/DataTable"
import Tooltip from "aldoo-ra/Tooltip"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import {
  listNotificationRules,
  deleteNotificationRule,
  testNotificationRule,
  listNotificationEvents,
  notificationEventsStats,
  resolveNotificationEvents,
  deleteNotificationEvents,
} from "../api"
import RuleEditor from "./RuleEditor"
import ChannelTargetsManager from "./ChannelTargetsManager"
import { useNotificationStream } from "./realtime"

const baseTabs = [
  { value: "events", label: "Events" },
  { value: "rules", label: "Notifications" },
  { value: "channels", label: "Channels" },
]

const NotificationsPage = () => {
  const [auth] = PlatformAuth.useAuth()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted || auth) return
    navigate("/")
    PubSub.publish("signin", { redirect: "/notifications" })
  }, [mounted, auth])

  const [orgs, setOrgs] = useState([])
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [tab, _setTab] = useState(() => {
    if (typeof window === "undefined") return "events"
    const t = new URLSearchParams(window.location.search).get("tab")
    return ["rules", "events", "channels"].includes(t) ? t : "events"
  })

  const setTab = (next) => {
    _setTab(next)
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    params.set("tab", next)
    const url = `${window.location.pathname}?${params.toString()}`
    if (window.location.pathname + window.location.search !== url) {
      window.history.pushState({}, "", url)
    }
  }

  // popstate → re-read tab from URL
  useEffect(() => {
    if (typeof window === "undefined") return
    const onPop = () => {
      const t = new URLSearchParams(window.location.search).get("tab")
      _setTab(["rules", "events", "channels"].includes(t) ? t : "events")
    }
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])
  const [rules, setRules] = useState([])
  const [editingRule, setEditingRule] = useState(null)
  const [events, setEvents] = useState([])
  const [stats, setStats] = useState({ open: 0, resolved: 0, total: 0 })

  // Event filters — hydrate from URL query
  const initialFilters = (() => {
    if (typeof window === "undefined") return { p: [], c: [], s: [], r: [] }
    const sp = new URLSearchParams(window.location.search)
    const split = (k) => (sp.get(k) || "").split(",").filter(Boolean)
    return { p: split("projectIds"), c: split("componentIds"), s: split("statuses"), r: split("ruleIds") }
  })()
  const [filterProjectIds, _setFilterProjectIds] = useState(initialFilters.p)
  const [filterComponentIds, _setFilterComponentIds] = useState(initialFilters.c)
  const [filterStatuses, _setFilterStatuses] = useState(initialFilters.s)
  const [filterRuleIds, _setFilterRuleIds] = useState(initialFilters.r)

  const syncFilterToUrl = (key, values) => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    if (values.length) params.set(key, values.join(","))
    else params.delete(key)
    const url = `${window.location.pathname}?${params.toString()}`
    if (window.location.pathname + window.location.search !== url) {
      window.history.replaceState({}, "", url)
    }
  }
  const setFilterProjectIds = (v) => { _setFilterProjectIds(v); syncFilterToUrl("projectIds", v) }
  const setFilterComponentIds = (v) => { _setFilterComponentIds(v); syncFilterToUrl("componentIds", v) }
  const setFilterStatuses = (v) => { _setFilterStatuses(v); syncFilterToUrl("statuses", v) }
  const setFilterRuleIds = (v) => { _setFilterRuleIds(v); syncFilterToUrl("ruleIds", v) }
  const [allProjects, setAllProjects] = useState([])
  const [allComponents, setAllComponents] = useState([])

  useEffect(() => {
    if (!mounted || !auth) return
    ;(async () => {
      const r = await listOrgs()
      const list = r?.orgs || r?.organizations || r?.list || []
      setOrgs(list)
      if (list.length && !selectedOrg) {
        setSelectedOrg({ value: String(list[0]._id), label: list[0].name })
      }
    })()
  }, [mounted, auth])

  const refreshRules = async () => {
    if (!selectedOrg?.value) return
    const r = await listNotificationRules(selectedOrg.value)
    setRules(r?.rules || [])
  }

  const [eventsRefreshKey, setEventsRefreshKey] = useState(0)

  const refreshEvents = async () => {
    // Refresh stats + trigger DataTable to re-fetch.
    await refreshStats()
    setEventsRefreshKey((k) => k + 1)
  }

  // Adapter: DataTable expects apiMethod({page, itemsPerPage, search, sort, ...}) returning {events, total}.
  const eventsApiMethod = async ({ page, itemsPerPage }) => {
    if (!selectedOrg?.value) return { events: [], total: 0 }
    const r = await listNotificationEvents({
      orgId: selectedOrg.value,
      limit: itemsPerPage,
      skip: (page - 1) * itemsPerPage,
      projectIds: filterProjectIds.length ? filterProjectIds : undefined,
      componentIds: filterComponentIds.length ? filterComponentIds : undefined,
      statuses: filterStatuses.length ? filterStatuses : undefined,
      ruleIds: filterRuleIds.length ? filterRuleIds : undefined,
    })
    return { events: r?.events || [], total: r?.total || 0 }
  }

  // Load projects + components for filter dropdowns
  useEffect(() => {
    if (!selectedOrg?.value) return
    listProjects(selectedOrg.value).then(async (r) => {
      const projects = r?.projects || []
      setAllProjects(projects)
      const perProject = await Promise.all(projects.map(async (p) => {
        const cr = await listComponents(p._id)
        return (cr?.components || []).map((c) => ({ ...c, _projectName: p.name }))
      }))
      setAllComponents(perProject.flat())
    })
  }, [selectedOrg?.value])

  // Re-fetch when filters change (bump refreshKey -> DataTable controller re-runs apiMethod)
  useEffect(() => {
    if (tab !== "events") return
    setEventsRefreshKey((k) => k + 1)
    refreshStats()
  }, [JSON.stringify(filterProjectIds), JSON.stringify(filterComponentIds), JSON.stringify(filterStatuses), JSON.stringify(filterRuleIds)])

  const refreshStats = async () => {
    if (!selectedOrg?.value) return
    const s = await notificationEventsStats({ orgId: selectedOrg.value })
    setStats({ open: s?.open || 0, resolved: s?.resolved || 0, total: s?.total || 0 })
  }

  useEffect(() => {
    if (!selectedOrg?.value) return
    refreshRules() // used by the Events tab for summary fallback when context is missing
    // refreshEvents already refreshes stats; avoid a second stats call.
    if (tab === "events") refreshEvents()
    else refreshStats() // always know the open count so the Events tab can show a badge
  }, [selectedOrg?.value, tab])

  // Live updates — refresh stats always, full events list only when on the tab
  useNotificationStream({
    orgId: selectedOrg?.value,
    onEvent: () => {
      // refreshEvents includes a stats refresh; don't double up.
      if (tab === "events") refreshEvents()
      else refreshStats()
    },
  })

  const onResolveAll = async () => {
    if (!selectedOrg?.value) return
    const ok = await showPromptDialogAsync({
      title: "Resolve all open",
      message: "Resolve all currently visible open events?",
      confirmText: "Resolve",
      cancelText: "Cancel",
    })
    if (!ok) return
    await resolveNotificationEvents({ orgId: selectedOrg.value, filter: { status: "open" } })
    refreshEvents()
  }

  const onResolveOne = async (eventId) => {
    await resolveNotificationEvents({ orgId: selectedOrg.value, eventIds: [eventId] })
    refreshEvents()
  }

  const [selectedEventIds, setSelectedEventIds] = useState([])
  const [selectedEventData, setSelectedEventData] = useState([])

  const onResolveSelected = async () => {
    if (selectedEventIds.length === 0) return
    await resolveNotificationEvents({ orgId: selectedOrg.value, eventIds: selectedEventIds })
    setSelectedEventIds([])
    setSelectedEventData([])
    refreshEvents()
  }

  const onDeleteSelected = async () => {
    if (selectedEventIds.length === 0) return
    const ok = await showPromptDialogAsync({
      title: "Delete events",
      message: `Permanently delete ${selectedEventIds.length} event(s)? This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    })
    if (!ok) return
    await deleteNotificationEvents({ orgId: selectedOrg.value, eventIds: selectedEventIds })
    setSelectedEventIds([])
    setSelectedEventData([])
    refreshEvents()
  }

  const onTestRule = async (ruleId) => {
    const r = await testNotificationRule(ruleId)
    if (r?.error) {
      showMessageDialog({ title: "Test failed", message: r.error, btnLabel: "OK" })
      return
    }
    const ok = (r?.results || []).filter((x) => x.ok).length
    const fail = (r?.results || []).filter((x) => !x.ok).length
    showMessageDialog({
      title: "Test sent",
      message: `Successful: ${ok}\nFailed: ${fail}`,
      btnLabel: "OK",
    })
  }

  // ---- DataTable column defs ----------------------------------------------

  const ruleColumns = [
    {
      id: "name",
      title: "Name",
      render: (_, r) => (
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{r.name}</span>
          {!r.enabled && <span className="text-xs px-2 py-0.5 rounded bg-neutral-200 text-neutral-700">Disabled</span>}
          {r.urgentMode?.enabled && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Urgent</span>}
        </div>
      ),
    },
    {
      id: "condition",
      title: "Condition",
      render: (_, r) => <span className="text-xs text-text_secondary">{r.condition?.kind}</span>,
    },
    {
      id: "scope",
      title: "Scope",
      render: (_, r) => (
        <span className="text-xs text-text_secondary">
          {r.scope?.projectId ? "Project" : "Any project"}
          {r.scope?.componentId ? " · Component" : ""}
          {r.scope?.appIdPattern ? ` · ${r.scope.appIdPattern}` : ""}
        </span>
      ),
    },
    {
      id: "subscribers",
      title: "Subscribers",
      render: (_, r) => <span className="text-xs">{r.subscribers?.length || 0}</span>,
    },
    {
      id: "actions",
      title: "",
      render: (_, r) => (
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => onTestRule(r._id)} className="text-xs px-3 py-1">Test</Button>
          <Button variant="secondary" onClick={() => setEditingRule(r)} className="text-xs px-3 py-1">Edit</Button>
          <Button
            variant="secondary"
            onClick={async () => {
              const ok = await showPromptDialogAsync({
                title: "Delete rule",
                message: `Delete rule "${r.name}"? This cannot be undone.`,
                confirmText: "Delete",
                cancelText: "Cancel",
              })
              if (!ok) return
              await deleteNotificationRule(r._id)
              refreshRules()
            }}
            className="text-xs px-3 py-1 text-error border-error hover:bg-error/10"
          >Delete</Button>
        </div>
      ),
    },
  ]

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime()
    const s = Math.max(0, Math.round(diff / 1000))
    if (s < 60) return `${s}s ago`
    const m = Math.round(s / 60)
    if (m < 60) return `${m} min ago`
    const h = Math.round(m / 60)
    if (h < 24) return `${h}h ago`
    const d = Math.round(h / 24)
    return `${d}d ago`
  }

  const eventColumns = [
    {
      id: "openedAt",
      title: "Opened",
      render: (_, e) => (
        <Tooltip content={new Date(e.openedAt).toLocaleString()} display="inline-block">
          <span className="text-xs text-text_secondary whitespace-nowrap">{timeAgo(e.openedAt)}</span>
        </Tooltip>
      ),
    },
    {
      id: "where",
      title: "Where",
      render: (_, e) => {
        const project = allProjects.find((p) => String(p._id) === String(e.projectId))
        const logo = project?.logo
        const projectName = e.context?.projectName || project?.name || "—"
        const orgSlug = (selectedOrg?.label || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
        const projectSlug = e.context?.projectSlug || project?.slug
        const componentName = e.context?.componentName || "—"
        const canLink = orgSlug && projectSlug && e.componentId
        const target = canLink ? `/projects/${orgSlug}/${projectSlug}/${e.componentId}?days=1` : null
        return (
          <div className="flex items-center gap-2 text-sm leading-tight">
            {logo ? (
              <img src={logo} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
            ) : (
              <div className="w-6 h-6 rounded bg-neutral_1 flex items-center justify-center text-xs text-text_secondary shrink-0 uppercase">
                {(projectName || "?").slice(0, 1)}
              </div>
            )}
            {target ? (
              <a
                href={target}
                onClick={(ev) => { ev.preventDefault(); navigate(target) }}
                className="font-medium truncate hover:text-link transition-colors"
              >
                {componentName}
              </a>
            ) : (
              <div className="font-medium truncate">{componentName}</div>
            )}
          </div>
        )
      },
    },
    {
      id: "title",
      title: "Trigger",
      render: (_, e) => {
        const c = e.context || {}
        // Fallback: if event was created before we snapshotted condition into context,
        // look up the rule from the current list.
        const liveRule = rules.find((r) => String(r._id) === String(e.ruleId))
        const kind = c.conditionKind || liveRule?.condition?.kind
        const p = (c.conditionParams && Object.keys(c.conditionParams).length ? c.conditionParams : liveRule?.condition?.params) || {}
        let summary = ""
        switch (kind) {
          case "downtime_continuous":
            summary = p.minSeconds ? `Down ≥ ${Math.round(p.minSeconds / 60)}min` : ""
            break
          case "downtime_cumulative":
            if (p.totalSeconds && p.windowSeconds) {
              summary = `Total down ≥ ${Math.round(p.totalSeconds / 60)}min in ${Math.round(p.windowSeconds / 3600)}h`
            }
            break
          case "latency_threshold":
            if (p.ms) {
              summary = p.sustainedSeconds > 0
                ? `Latency ≥ ${p.ms}ms for ${Math.round(p.sustainedSeconds / 60)}min`
                : `Latency peak ≥ ${p.ms}ms`
            }
            break
          case "check_failure":
            summary = p.consecutiveFailures ? `${p.consecutiveFailures} consecutive failures` : ""
            break
          case "check_specific_failure":
            summary = "Specific check failed"
            break
          default:
            summary = kind || ""
        }
        // Older events stored the full "Rule — Project › Component › Check" in title; strip suffix as fallback.
        const ruleName = c.ruleName || liveRule?.name || (e.title ? e.title.split(" — ")[0] : "—")

        const tooltipLines = []
        if (c.latencyMs != null) tooltipLines.push(`Latency: ${c.latencyMs}ms`)
        if (c.statusCode != null) tooltipLines.push(`HTTP: ${c.statusCode}`)
        if (c.consecutiveFailures) tooltipLines.push(`Consecutive failures: ${c.consecutiveFailures}`)
        if (c.errorType) tooltipLines.push(`Error: ${c.errorType}${c.errorMsg ? " — " + c.errorMsg : ""}`)
        if (e.message) tooltipLines.push(e.message)
        const tooltipContent = (
          <div className="space-y-1 text-xs">
            <div className="font-semibold">{ruleName}</div>
            {summary && <div className="opacity-80">{summary}</div>}
            <div className="border-t border-white/20 my-1" />
            {tooltipLines.length > 0
              ? tooltipLines.map((l, i) => <div key={i}>{l}</div>)
              : <div className="opacity-70">No measurement captured</div>}
          </div>
        )

        return (
          <Tooltip content={tooltipContent} display="block">
            <div className="leading-tight">
              <div className="text-sm font-medium truncate">{ruleName}</div>
              {summary && <div className="text-xs text-text_secondary truncate">{summary}</div>}
            </div>
          </Tooltip>
        )
      },
    },
    {
      id: "actions",
      title: "",
      render: (_, e) => (
        <div className="flex items-center justify-end gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${e.status === "open" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{e.status}</span>
          {e.status === "open" && (
            <Button variant="secondary" onClick={() => onResolveOne(e._id)} className="text-xs px-3 py-1">Resolve</Button>
          )}
        </div>
      ),
    },
  ]

  if (!mounted || !auth) return null

  const orgOptions = orgs.map((o) => ({ value: String(o._id), label: o.name }))

  return (
    <div>
      <Navbar />
      <div className="mt-16 p-6 w-full">
        <div className="flex items-center justify-between mb-4 gap-3">
          <Typography variant="admin_h2">Notifications</Typography>
          <DropdownSelect value={selectedOrg} onChange={(v) => setSelectedOrg(v?.[0] || v)} options={orgOptions} />
        </div>

        <Tabs
          tabs={baseTabs.map((t) => (
            t.value === "events" && stats.open > 0
              ? { ...t, badge: stats.open }
              : t
          ))}
          value={tab}
          onChange={setTab}
          styleOverrides={{
            tabBadge: "ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full font-medium",
          }}
        />

        {tab === "rules" && (
          <div className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setEditingRule({})} disabled={!selectedOrg?.value}>+ Notification</Button>
            </div>

            <div className="mt-4">
              <DataTable
                data={rules}
                renders={ruleColumns}
                searchField="name description"
                itemsPerPage={20}
                localUserPreferencesKey="aldoo-status.notifications.rules"
                styleOverrides={{
                  header: "flex flex-row justify-left items-center mb-12",
                  row: "h-[60px] text-text text-sm font-normal border-b border-bordered hover:bg-canvas transition-colors",
                }}
                noData={<div className="text-text_secondary text-sm mt-12">No rules yet. Click + Notification to create one.</div>}
              />
            </div>

            {editingRule && (
              <RuleEditor
                orgId={selectedOrg.value}
                rule={editingRule._id ? editingRule : null}
                onClose={() => { setEditingRule(null); refreshRules() }}
              />
            )}
          </div>
        )}

        {tab === "events" && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 rounded-lg border border-bordered bg-paper">
                <div className="text-xs text-text_secondary uppercase">Open</div>
                <div className="text-2xl font-bold text-red-600 mt-1">{stats.open}</div>
              </div>
              <div className="p-4 rounded-lg border border-bordered bg-paper">
                <div className="text-xs text-text_secondary uppercase">Resolved</div>
                <div className="text-2xl font-bold text-green-600 mt-1">{stats.resolved}</div>
              </div>
              <div className="p-4 rounded-lg border border-bordered bg-paper">
                <div className="text-xs text-text_secondary uppercase">Total</div>
                <div className="text-2xl font-bold mt-1">{stats.total}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block mb-1 text-xs font-medium text-text_secondary">Project</label>
                <DropdownSelect
                  value={filterProjectIds.map((id) => {
                    const p = allProjects.find((x) => String(x._id) === id)
                    return { value: id, label: p?.name || id, logo: p?.logo }
                  })}
                  onChange={(v) => setFilterProjectIds((Array.isArray(v) ? v : [v]).map((x) => x.value))}
                  options={allProjects.map((p) => ({ value: String(p._id), label: p.name, logo: p.logo }))}
                  multiSelection
                  searchEnabled
                  showCheckbox
                  renderOption={(opt) => (
                    <div className="flex items-center gap-2 py-1">
                      {opt.logo ? (
                        <img src={opt.logo} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                      ) : (
                        <div className="w-5 h-5 rounded bg-neutral_1 flex items-center justify-center text-[10px] text-text_secondary shrink-0 uppercase">
                          {(opt.label || "?").slice(0, 1)}
                        </div>
                      )}
                      <span className="text-sm truncate">{opt.label}</span>
                    </div>
                  )}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs font-medium text-text_secondary">Component</label>
                <DropdownSelect
                  value={filterComponentIds.map((id) => ({ value: id, label: allComponents.find((c) => String(c._id) === id)?.name || id }))}
                  onChange={(v) => setFilterComponentIds((Array.isArray(v) ? v : [v]).map((x) => x.value))}
                  options={allComponents.map((c) => ({ value: String(c._id), label: `${c._projectName} › ${c.name}` }))}
                  multiSelection
                  searchEnabled
                  showCheckbox
                />
              </div>
              <div>
                <label className="block mb-1 text-xs font-medium text-text_secondary">Status</label>
                <DropdownSelect
                  value={filterStatuses.map((s) => ({ value: s, label: s }))}
                  onChange={(v) => setFilterStatuses((Array.isArray(v) ? v : [v]).map((x) => x.value))}
                  options={[{ value: "open", label: "Open" }, { value: "resolved", label: "Resolved" }]}
                  multiSelection
                  showCheckbox
                />
              </div>
              <div>
                <label className="block mb-1 text-xs font-medium text-text_secondary">Trigger</label>
                <DropdownSelect
                  value={filterRuleIds.map((id) => ({ value: id, label: rules.find((r) => String(r._id) === id)?.name || id }))}
                  onChange={(v) => setFilterRuleIds((Array.isArray(v) ? v : [v]).map((x) => x.value))}
                  options={rules.map((r) => ({ value: String(r._id), label: r.name }))}
                  multiSelection
                  searchEnabled
                  showCheckbox
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              {selectedEventIds.length > 0 && (
                <>
                  <Button variant="secondary" onClick={onResolveSelected}>Resolve selected ({selectedEventIds.length})</Button>
                  <Button variant="secondary" onClick={onDeleteSelected} className="text-error border-error hover:bg-error/10">Delete selected ({selectedEventIds.length})</Button>
                </>
              )}
              <Button variant="secondary" onClick={onResolveAll} disabled={stats.open === 0}>Resolve all open</Button>
            </div>

            <div className="mt-4">
              <DataTable
                apiMethod={eventsApiMethod}
                apiDataField="events"
                refreshKey={eventsRefreshKey}
                renders={eventColumns}
                searchField="title message"
                itemsPerPage={20}
                localUserPreferencesKey="aldoo-status.notifications.events"
                enableSelection
                selectedItems={selectedEventIds}
                selectedData={selectedEventData}
                onSelectionChange={(ids, data) => { setSelectedEventIds(ids); setSelectedEventData(data) }}
                onDataLoaded={(data) => { setEvents(data); return data }}
                styleOverrides={{
                  header: "flex flex-row justify-left items-center mb-12",
                  row: "h-[60px] text-text text-sm font-normal border-b border-bordered hover:bg-canvas transition-colors",
                }}
                noData={<div className="text-text_secondary text-sm mt-12">No events yet. They will appear here once a rule fires.</div>}
              />
            </div>
          </div>
        )}

        {tab === "channels" && selectedOrg && (
          <div className="mt-4">
            <ChannelTargetsManager orgId={selectedOrg.value} />
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
